import {
  Badge,
  Button,
  createDataTableColumnHelper,
  DataTable,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Link as UiLink,
  SearchField,
} from "@beweegbaas/ui";
import {
  createFileRoute,
  Link,
  Outlet,
  stripSearchParams,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "@beweegbaas/ui";
import { DataTableActions } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { submitDeleteKlas } from "#/modules/school.klassen/classes.functions";
import {
  CLASSES_SEARCH_DEFAULTS,
  classesSearchSchema,
} from "#/modules/school.klassen/schema.search";
import { getLeerjaarTranslation } from "#/modules/util.i18n/enum-mapping";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import {
  klassenOverviewQueryOptions,
  KLASSEN_OVERVIEW_QUERY_KEY,
} from "#/modules/school.klassen/query.klassen-overview";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/classes")({
  validateSearch: classesSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(CLASSES_SEARCH_DEFAULTS)] },
  // All search keys feed the query, so they belong in the match identity — entering with
  // different filters re-runs the loader (staleTime only skips same-deps re-entries).
  loaderDeps: ({ search }) => search,
  staleTime: Infinity,
  // Primes the same query the component reads; loader data doubles as the render fallback
  // when the query cache entry was GC'd.
  loader: ({ context, params: { school, schoolyear }, deps }) =>
    context.queryClient.ensureQueryData(klassenOverviewQueryOptions({ school, schoolyear }, deps)),
  component: ClassesPage,
  errorComponent: ClassesError,
});

type KlasRow = components["schemas"]["KlasOverviewModel"];

const helper = createDataTableColumnHelper<KlasRow>();

function ClassesPage() {
  const { locale, school, schoolyear } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const router = useRouter();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const submitDeleteKlasFn = useServerFn(submitDeleteKlas);
  const search = Route.useSearch();
  const overview = useOverviewSearch(search, navigate);

  // Data reads live in TanStack Query: the query key is the typed, match-synced `Route.useSearch()`
  // value, so filter/sort/page changes are served by this query (keepPreviousData → stale rows stay
  // visible + dimmed while the next result fetches), NOT by re-running the route loader. The loader
  // already primed this exact key, so the cache entry carries a truthful `dataUpdatedAt` and goes
  // stale on schedule; `loaderData` is only the render fallback if that entry was GC'd (in which
  // case this query is refetching, and `isListPending` dims the rows).
  const overviewQuery = useQuery({
    ...klassenOverviewQueryOptions({ school, schoolyear }, search),
    placeholderData: keepPreviousData,
  });
  const { classes, totalPages, totalCount } = overviewQuery.data ?? loaderData;

  // During a deps-change transition the mounted query (old key) isn't fetching, so the dim
  // needs the router's pending state too.
  const isNavigating = useRouterState({ select: (s) => s.isLoading });
  const isListPending = overviewQuery.isFetching || isNavigating;

  const showEmptyState = classes.length === 0 && !overview.hasSearchTerm;

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("name", {
          header: "Naam",
          enableSorting: true,
          meta: { rowTarget: true },
          cell: ({ row, getValue }) => (
            <UiLink
              variant="secondary"
              underline
              to="/$locale/{-$schoolyear}/$school/students/overview"
              params={{ locale, schoolyear, school }}
              search={{ klasId: row.original.id.toString() }}
            >
              {getValue()}
            </UiLink>
          ),
        }),
        helper.display({
          id: "grades",
          header: "Leerjaar",
          meta: { headerClassName: "w-full" },
          cell: ({ row }) => (
            <div className="flex flex-wrap gap-2">
              {row.original.leerjaren.map((leerjaar, index) => (
                <Badge key={index} variant="blue" size="sm">
                  {getLeerjaarTranslation(leerjaar)}
                </Badge>
              ))}
            </div>
          ),
        }),
        helper.accessor("leerlingCount", {
          header: "Leerlingen",
          enableSorting: true,
          meta: { headerClassName: "whitespace-nowrap" },
        }),
        helper.display({
          id: "actions",
          header: "",
          meta: { action: true, headerClassName: "w-0" },
          cell: ({ row }) => (
            <DataTableActions
              actions={[
                {
                  key: "edit",
                  label: m["klassen.bewerk"](),
                  icon: <IconMaterialSymbolsEdit className="size-4" />,
                  render: (
                    <Link
                      from={Route.fullPath}
                      to="/$locale/{-$schoolyear}/$school/classes/$klasId/modal"
                      params={(prev: Record<string, string>) => ({
                        ...prev,
                        klasId: row.original.id.toString(),
                      })}
                      search={(prev) => prev}
                    />
                  ),
                },
                {
                  key: "delete",
                  label: m["klassen.verwijder"](),
                  icon: <IconMaterialSymbolsDeleteOutline className="size-4" />,
                  destructive: true,
                  confirm: {},
                  onClick: async () => {
                    await submitDeleteKlasFn({
                      data: { klasId: row.original.id.toString() },
                    });
                    toast.success(m["klassen.verwijderd_toast"]());
                    // List data lives in TanStack Query — invalidate its keys so the
                    // active useQuery refetches; router.invalidate() re-runs the loader
                    // (first-paint snapshot + any sibling route data).
                    await Promise.all([
                      queryClient.invalidateQueries({
                        queryKey: [{ school, schoolyear }, KLASSEN_OVERVIEW_QUERY_KEY],
                      }),
                      router.invalidate(),
                    ]);
                  },
                },
              ]}
            />
          ),
        }),
      ]),
    [submitDeleteKlasFn, queryClient, router, locale, school, schoolyear],
  );

  return (
    <Page>
      {!showEmptyState && (
        <Page.Header>
          <SearchField
            placeholder={m["common.placeholders.search_by_name"]()}
            className="w-auto grow md:max-w-[320px]"
            {...overview.searchFieldProps}
          />
          <Page.Header.Actions>
            <Button
              className="max-sm:w-full max-sm:grow sm:ml-auto"
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/classes/modal"
                  params={(prev: Record<string, string>) => prev}
                  search={(prev) => prev}
                />
              }
              nativeButton={false}
              variant="default"
            >
              Voeg een klas toe
              <IconMaterialSymbolsAdd />
            </Button>
          </Page.Header.Actions>
        </Page.Header>
      )}
      {showEmptyState && <Page.MobileHeader />}
      {showEmptyState ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{m["school.klassen.overview.empty"]()}</EmptyTitle>
            <EmptyDescription>
              Importeer leerlingen om klassen aan te maken en hier een overzicht te zien.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size="xl"
              variant="default"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                  params={(prev: Record<string, string>) => ({
                    ...prev,
                    type: "modify-existing-year",
                  })}
                />
              }
              nativeButton={false}
            >
              {m["common.empty_state.import_students"]()}
              <IconMaterialSymbolsArrowRightAltRounded />
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <PendingContent pending={isListPending}>
          <DataTable
            alignToContainer
            aria-label="Klassen"
            columns={columns}
            data={classes}
            getRowId={(row) => row.id.toString()}
            empty={m["school.klassen.overview.empty"]()}
            {...overview.sortProps}
          />
          <Page.Container>
            <Pager
              totalPages={totalPages}
              totalCount={totalCount}
              className={"border-0"}
              {...overview.pagerProps}
            />
          </Page.Container>
        </PendingContent>
      )}

      <Outlet />
    </Page>
  );
}

function ClassesError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
